import { useCallback, useEffect, useState } from 'react';
import { ImageIcon, Star } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { Avatar, EmptyState, HeroHeader, Lightbox, Stars } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Reviews.module.css';

type ClientReview = {
  id: string;
  rating: number;
  review_comment: string | null;
  completion_photo: string | null;
  created_at: string;
  client_name: string;
  service_name: string;
};

export default function ProviderReviews() {
  const userId = useUserId();
  const [reviews, setReviews] = useState<ClientReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('bookings')
        .select(
          'id, rating, review_comment, completion_photo, created_at, services(name), profiles!client_id(full_name)'
        )
        .eq('provider_id', userId)
        .eq('status', 'completed')
        .not('rating', 'is', null)
        .order('created_at', { ascending: false });

      setReviews(
        (data ?? []).map((row: any) => ({
          id: row.id,
          rating: Number(row.rating ?? 0),
          review_comment: row.review_comment ?? null,
          completion_photo: row.completion_photo ?? null,
          created_at: row.created_at,
          client_name: row.profiles?.full_name ?? 'Client',
          service_name: row.services?.name ?? 'Service',
        }))
      );
    } catch (err) {
      console.error('Error loading provider reviews:', err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={styles.page}>
      <HeroHeader
        title="Avis clients"
        subtitle={`${reviews.length} avis reçu${reviews.length > 1 ? 's' : ''}`}
      />

      <div className={styles.body}>
        {loading ? (
          <Spinner block />
        ) : reviews.length === 0 ? (
          <EmptyState
            icon={<Star size={30} fill="currentColor" />}
            title="Aucun avis pour le moment"
            text="Les avis des clients apparaîtront ici après les missions terminées."
          />
        ) : (
          <ul className={styles.list}>
            {reviews.map((review) => (
              <li key={review.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <Avatar name={review.client_name} size={46} />
                  <div className={styles.cardInfo}>
                    <p className={styles.clientName}>{review.client_name}</p>
                    <p className={styles.serviceName}>{review.service_name}</p>
                  </div>
                  <span className={styles.date}>{formatDate(review.created_at)}</span>
                </div>

                <div className={styles.ratingRow}>
                  <Stars value={review.rating} size={18} />
                  <span className={styles.ratingText}>{review.rating} / 5</span>
                </div>

                {review.review_comment ? (
                  <p className={styles.comment}>{review.review_comment}</p>
                ) : (
                  <p className={styles.noComment}>Pas de commentaire</p>
                )}

                {review.completion_photo ? (
                  <button
                    type="button"
                    className={styles.photoBtn}
                    onClick={() => setLightbox(review.completion_photo)}
                  >
                    <img className={styles.photo} src={review.completion_photo} alt="Résultat" />
                  </button>
                ) : (
                  <p className={styles.noPhoto}>
                    <ImageIcon size={19} /> Aucune photo partagée
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
